import { z } from 'zod';
import { PortalAppointmentDetailSchema } from './portal.js';
import { LocalTimeSchema } from './scheduling.js';
/**
 * The earlier-slot waitlist (M10.3, FR-BOOK-16, ADR-023, `DATABASE.md` §7.5).
 *
 * A waitlist entry is a standing instruction attached to a **booked** appointment: "if
 * something opens up between these two instants, and it suits me, offer it to me". It is
 * never a booking of its own - the patient always already has a visit, and the waitlist
 * only ever moves it earlier.
 *
 * ## One live offer per entry, and the entry is the offer
 *
 * When a slot frees, the matcher holds it with an ordinary `slot_reservations` row and
 * writes the offer onto the entry (`offered_start_at`, `offer_expires_at`). There is no
 * separate offers table because there is never more than one live offer per entry: a
 * patient holding two offers for the same appointment is two holds on the calendar for
 * one person, which is exactly what the reservation index exists to prevent.
 *
 * A freed slot may be offered to **several** entries at once; the first accept wins on
 * the reservation, and the losers are told the offer closed. That race is settled by the
 * database, not by application code that looked first.
 *
 * ## No PHI in an offer
 *
 * The entry carries ids, instants and a weekday list. The email that announces an offer
 * carries the clinic's name, a time and a link (`notification-kinds.ts`); the reason for
 * the visit never appears in either.
 */
/**
 * `waitlist_entries.status`, exactly as `ck_waitlist_status` spells it.
 *
 * `cancelled` is the patient leaving the list; `declined` is reserved for an entry that
 * turned an offer down and did not want another. Declining one offer does **not** use
 * it - that returns the entry to `active`, because "not that Tuesday" is not "stop
 * looking". The vocabulary is the database's so that a status can only ever be added by
 * a migration.
 */
export const WAITLIST_STATUSES = [
    'active',
    'offered',
    'accepted',
    'declined',
    'expired',
    'cancelled',
];
export const WaitlistStatusSchema = z.enum(WAITLIST_STATUSES);
/*
 * The time-of-day bounds reuse `scheduling.ts`'s `LocalTimeSchema`: a clinic-local
 * `HH:MM`, never an instant, because "not before nine" is a fact about the patient's
 * mornings and not about any particular day - and the days it applies to have not
 * happened yet. It is the same rule a provider's schedule uses, so one definition.
 */
/** An earlier slot the clinic is holding for this patient until `expiresAt`. */
export const WaitlistOfferSchema = z
    .object({
    startAt: z.iso.datetime(),
    endAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    /** Who the earlier visit would be with. Null when the clinic publishes no names. */
    providerName: z.string().max(200).nullable().default(null),
})
    .strict();
export const WaitlistEntrySchema = z
    .object({
    id: z.uuid(),
    appointmentId: z.uuid(),
    /** The visit being moved; repeated here so a list needs one call. */
    appointmentStartAt: z.iso.datetime(),
    typeName: z.string().max(200),
    earliestAt: z.iso.datetime(),
    latestAt: z.iso.datetime(),
    /** 0 = Sunday. Empty means every day. */
    preferredWeekdays: z.array(z.int().min(0).max(6)).max(7).default([]),
    earliestTimeLocal: LocalTimeSchema.nullable().default(null),
    latestTimeLocal: LocalTimeSchema.nullable().default(null),
    /** "Only with the clinician I already have", expressed as their id. */
    preferredProviderId: z.uuid().nullable().default(null),
    clinicTimeZone: z.string().max(60),
    status: WaitlistStatusSchema,
    offer: WaitlistOfferSchema.nullable().default(null),
    createdAt: z.iso.datetime(),
})
    .strict();
/**
 * `POST /patient/waitlist`.
 *
 * `latestAt` is capped at the appointment's own start by the API: a waitlist for a slot
 * *after* the visit the patient already has is not a waitlist, it is a reschedule, and
 * it has its own endpoint with its own policy window.
 */
export const JoinWaitlistRequestSchema = z
    .object({
    appointmentId: z.uuid(),
    /** The earliest instant the patient would take. Both ends are their choice. */
    earliestAt: z.iso.datetime({ offset: true }),
    latestAt: z.iso.datetime({ offset: true }),
    preferredWeekdays: z.array(z.int().min(0).max(6)).max(7).optional(),
    earliestTimeLocal: LocalTimeSchema.optional(),
    latestTimeLocal: LocalTimeSchema.optional(),
    /**
     * `true` keeps the clinician they already have. The provider id is resolved from the
     * appointment rather than taken from the body: a patient naming a clinician they
     * have never seen is a request the licensing rules would refuse anyway, and taking
     * an id here would be an enumeration oracle for the clinic's roster.
     */
    sameProviderOnly: z.boolean().optional(),
})
    .strict();
export const WaitlistListResponseSchema = z
    .object({ data: z.object({ entries: z.array(WaitlistEntrySchema) }).strict() })
    .strict();
export const WaitlistEntryResponseSchema = z
    .object({ data: z.object({ waitlistEntry: WaitlistEntrySchema }).strict() })
    .strict();
/**
 * `POST /patient/waitlist/:id/accept`.
 *
 * The appointment comes back in full because accepting an offer *is* a reschedule: the
 * screen that was showing "we found you something earlier" becomes the appointment
 * detail, and a second round trip to learn the new join window would be a spinner on a
 * confirmation.
 */
export const WaitlistAcceptResponseSchema = z
    .object({
    data: z
        .object({
        appointment: PortalAppointmentDetailSchema,
        waitlistEntry: WaitlistEntrySchema,
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=waitlist.js.map